import { MOT_DATA, MKT_DATA, DONEM_DATA, TAKIM_POS, TAKIM_NEG } from "../data/kalibrasyon";

/* ══════════════════════════════════════════════════════════════════════════
   Yardım — İstatistik & Piyasa Sapması rehberi.

   Amaç: kullanıcıya "biz neyi ölçüyoruz, sapma ne demek, bu tahmine nasıl
   giriyor" sorularını gerçek sayılarla anlatmak. Bütün rakamlar kalibrasyon
   modülünden (24.265 maçlık araştırma) okunur — İstatistikler sayfası tam
   veri tablosu, burası öğretici anlatım.
   ══════════════════════════════════════════════════════════════════════════ */

// ── Renk dili (üründe tutarlı) ───────────────────────────────────────────────
const HOME = "#2563eb", DRAW = "#d97706", AWAY = "#dc2626";
const POS  = "#1d5fbf";   // piyasa AZ fiyatlamış (gerçek daha yüksek)
const NEG  = "#c0392f";   // piyasa FAZLA fiyatlamış / sinyal tersine
const INK  = "#111827", SUB = "#6b7280", DIM = "#9ca3af", LINE = "#e5e7eb";

// ── pp yardımcıları ──────────────────────────────────────────────────────────
const ppNum = (v) => {
  const n = parseFloat(String(v).replace("pp", "").replace(",", "."));
  return isNaN(n) ? null : n;
};
const ppColor = (n) => (n == null ? DIM : n > 0 ? POS : n < 0 ? NEG : SUB);
const ppText  = (v) => {
  const n = ppNum(v);
  if (n == null) return "—";
  return `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n).toFixed(1)} pp`;
};

// ── Küçük görsel bileşenler ──────────────────────────────────────────────────
function Pp({ v, size = 13 }) {
  const n = ppNum(v);
  return (
    <span style={{ fontWeight: 800, color: ppColor(n), fontSize: size,
                   fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
      {ppText(v)}
    </span>
  );
}

// Piyasa vs Gerçek — iki yatay çubuk (motivasyon örnekleri için)
function VsBars({ piyasa, gercek, renk = POS }) {
  const enb = Math.max(piyasa, gercek, 1);
  return (
    <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
      {[["PİYASA", piyasa, "#cbd5e1"], ["GERÇEK", gercek, renk]].map(([ad, v, dolgu]) => (
        <div key={ad} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 9, letterSpacing: ".08em", color: DIM, width: 46, fontWeight: 700 }}>{ad}</span>
          <div style={{ flex: 1, height: 8, background: "#f1f5f9", borderRadius: 2 }}>
            <div style={{ width: `${Math.max((v / enb) * 100, 3)}%`, height: "100%", background: dolgu, borderRadius: 2 }} />
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, color: INK, width: 42, textAlign: "right",
                         fontVariantNumeric: "tabular-nums" }}>%{v}</span>
        </div>
      ))}
    </div>
  );
}

// Sezon ritmi sparkline — D1→D5
function SezonSpark({ d }) {
  const say = d.map((x) => ppNum(x) ?? 0);
  const enb = Math.max(...say.map(Math.abs), 3);
  const W = 220, H = 52, ort = H / 2;
  const x = (i) => 6 + (i / (say.length - 1)) * (W - 12);
  const y = (v) => ort - (v / enb) * (ort - 6);
  const cizgi = say.map((v, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(" ");
  const dip = say.indexOf(Math.min(...say));
  return (
    <svg width={W} height={H + 16} style={{ display: "block", marginTop: 4 }} aria-hidden="true">
      <line x1="0" y1={ort} x2={W} y2={ort} stroke={LINE} strokeWidth="1" strokeDasharray="2 3" />
      <path d={cizgi} fill="none" stroke={INK} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {say.map((v, i) => (
        <circle key={i} cx={x(i)} cy={y(v)} r={i === dip ? 4.5 : 3}
          fill={i === dip ? NEG : INK} stroke="#fff" strokeWidth="1.5" />
      ))}
      {["D1", "D2", "D3", "D4", "D5"].map((lbl, i) => (
        <text key={lbl} x={x(i)} y={H + 12} fontSize="9" fill={DIM} textAnchor="middle">{lbl}</text>
      ))}
    </svg>
  );
}

export default function Help() {
  // ── Stil sabitleri ──
  const card = { background: "#fff", border: `1px solid ${LINE}`, borderRadius: 10, padding: "16px 20px", marginBottom: 12 };
  const q = { fontSize: 14, fontWeight: 700, color: INK, marginBottom: 6 };
  const a = { fontSize: 14, color: "#374151", lineHeight: 1.7 };
  const sh = { fontSize: 17, fontWeight: 800, color: INK, margin: "34px 0 12px", display: "flex", alignItems: "center", gap: 9, letterSpacing: "-.01em" };
  const kicker = { fontSize: 11, fontWeight: 800, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 8 };
  const badge = (col) => ({
    display: "inline-block", padding: "2px 9px", borderRadius: 4, fontSize: 12, fontWeight: 700,
    background: col === "g" ? "#dcfce7" : col === "r" ? "#fee2e2" : col === "a" ? "#fef3c7" : "#dbeafe",
    color:      col === "g" ? "#166534" : col === "r" ? "#991b1b" : col === "a" ? "#92400e" : "#1e40af",
  });

  // ── Veriden örnekler ──
  const bulMkt   = (l) => MKT_DATA.find((r) => r.l === l);
  const bulDonem = (l) => DONEM_DATA.find((r) => r.l === l);
  const superLider   = MOT_DATA["Süper Lig"].ev.find((r) => r.s === "Şampiyon lider");        // rh 68.8 / mh 57.5
  const plLiderDep   = MOT_DATA["Premier League"].dep.find((r) => r.s === "Şampiyon lider");  // rh 55.6 / mh 37.4
  const laDusme      = MOT_DATA["La Liga"].ev.find((r) => r.s === "Düşme tehlikesi");         // rh 51.7 / mh 37.7
  const serieDonem   = bulDonem("Serie A");
  const eredDonem    = bulDonem("Eredivisie");

  // Piyasa edge tablosunda gösterilecek seçili ligler (en öğretici olanlar)
  const MKT_SHOW = ["Süper Lig", "Serie B", "La Liga", "Primeira Liga", "Premier League", "Scottish Prem."];

  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: "0 8px 56px" }}>

      {/* ── Başlık ── */}
      <div style={{ fontSize: 24, fontWeight: 800, color: INK, marginBottom: 4, letterSpacing: "-.02em" }}>
        Nasıl Çalışıyor
      </div>
      <div style={{ fontSize: 14.5, color: SUB, marginBottom: 8, lineHeight: 1.6 }}>
        Bahis oranlarının nerede sistematik olarak yanıldığını 24.265 maç üzerinde ölçüyoruz —
        sonra bu sapmaları her tahmine katıyoruz. Bu sayfa, hangi istatistiği neden çıkardığımızı
        ve bir "sapma"nın ne anlama geldiğini gerçek sayılarla anlatır.
      </div>

      {/* ══ 1. TEMEL İSTATİSTİK ══ */}
      <div style={sh}><span>📊</span> Önce ölçtüğümüz temel istatistikler</div>
      <div style={card}>
        <div style={a}>
          Her lig için 4 sezonun (2020–21, 2022–23, 2024–25, 2025–26) tüm maçlarını tarayıp
          şu temel dağılımları çıkarıyoruz:
          <ul style={{ marginTop: 10, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
            <li><strong style={{ color: HOME }}>Sonuç oranları</strong> — maçların yüzde kaçı ev galibiyeti,
              beraberlik, deplasman galibiyetiyle bitiyor. (Tipik olarak ev ~%45, beraberlik ~%26, deplasman ~%29;
              canlı değerler <em>İstatistikler</em> sayfasında lig lig.)</li>
            <li><strong style={{ color: DRAW }}>Gol dağılımı</strong> — maç başına ortalama gol, 0-0 oranı,
              en sık toplam gol sayısı ve o sayıya göre 1-X-2 olasılığı.</li>
            <li><strong style={{ color: AWAY }}>Ev / deplasman kırılımı</strong> — ev sahibi ve deplasman
              takımının attığı ortalama gol, kafa kafaya geçmiş.</li>
          </ul>
          <div style={{ marginTop: 10, fontSize: 13, color: SUB }}>
            Bunlar <strong>baseline</strong>'ımız: "bu ligde normalde ne olur" sorusunun cevabı.
            Asıl değer, gerçeğin bu baseline'dan — ve piyasadan — <em>nerede ayrıldığında</em> başlıyor.
          </div>
        </div>
      </div>

      {/* ══ 2. SAPMA NEDİR ══ */}
      <div style={sh}><span>🎯</span> "Sapma" (edge) tam olarak ne demek?</div>
      <div style={{ ...card, background: "#f8fafc", borderColor: "#dbe3ec" }}>
        <div style={a}>
          Bir bahis sitesi maça oran koyar; her orandan bir <strong>ima edilen olasılık</strong> çıkar
          (kabaca 1 ÷ oran). Örneğin ev sahibine <strong>2.00</strong> oran verilmişse piyasa o takıma
          yaklaşık <strong>%50</strong> şans biçmiş demektir.
          <div style={{ margin: "12px 0", padding: "12px 14px", background: "#fff", borderRadius: 8,
                        border: `1px solid ${LINE}`, fontSize: 14, lineHeight: 1.7 }}>
            <div style={{ fontWeight: 700, color: INK, marginBottom: 4 }}>Sapma = Gerçek sıklık − Piyasanın ima ettiği olasılık</div>
            Aynı türden yüzlerce geçmiş maçta o durum <strong>gerçekte</strong> ne sıklıkla yaşandı?
            İkisi arasındaki fark, <strong>yüzde puan</strong> (pp) cinsinden sapmadır.
          </div>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 6 }}>
            <span style={{ fontSize: 13.5 }}>
              <Pp v="+8.0" /> &nbsp;→ piyasa <strong>az</strong> fiyatlamış (gerçek daha yüksek çıkıyor)
            </span>
            <span style={{ fontSize: 13.5 }}>
              <Pp v="-8.0" /> &nbsp;→ piyasa <strong>fazla</strong> fiyatlamış / sinyal tersine
            </span>
          </div>
          <div style={{ marginTop: 12, fontSize: 13, color: SUB, lineHeight: 1.65 }}>
            Tek bir maçta bu fark gürültü olabilir. Biz sapmayı <strong>yüzlerce–binlerce maç</strong>
            üzerinde ölçtüğümüz için, kalıcı ve sistematik olanları ayırt edebiliyoruz. Aşağıdaki beş
            katman, sapmanın <em>nereden</em> geldiğini gösteriyor.
          </div>
        </div>
      </div>

      {/* ══ 3. BEŞ SAPMA KATMANI ══ */}
      <div style={sh}><span>🧩</span> Sapmanın beş katmanı</div>

      {/* 3.1 Piyasa Edge */}
      <div style={card}>
        <div style={{ ...kicker, color: POS }}>Katman 1 · Piyasa Edge</div>
        <div style={q}>Ligin kendine has fiyatlama hatası</div>
        <div style={a}>
          Her ligde piyasayı üç durumda test ediyoruz: <strong>büyük ev favorisi</strong> (A),
          <strong> deplasman</strong> (B) ve <strong>beraberlik</strong> (C). Aynı sinyal ligden lige
          bambaşka çalışır — bu yüzden tek bir "ev avantajı" katsayısı yerine lig lig ölçüyoruz.
        </div>
        <div style={{ overflowX: "auto", marginTop: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 460 }}>
            <thead>
              <tr style={{ color: DIM, fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em" }}>
                <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 700 }}>Lig</th>
                <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 700 }}>Ev fav. (A)</th>
                <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 700 }}>Deplasman (B)</th>
                <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 700 }}>Beraberlik (C)</th>
                <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 700 }}></th>
              </tr>
            </thead>
            <tbody>
              {MKT_SHOW.map((l) => {
                const r = bulMkt(l);
                if (!r) return null;
                return (
                  <tr key={l} style={{ borderTop: `1px solid ${LINE}` }}>
                    <td style={{ padding: "8px", fontWeight: 700, color: INK, whiteSpace: "nowrap" }}>
                      {l} <span style={{ color: DIM, fontWeight: 400, fontSize: 11 }}>· {r.n} maç</span>
                    </td>
                    <td style={{ padding: "8px", textAlign: "right" }}><Pp v={r.a} size={12.5} /></td>
                    <td style={{ padding: "8px", textAlign: "right" }}><Pp v={r.b} size={12.5} /></td>
                    <td style={{ padding: "8px", textAlign: "right" }}><Pp v={r.c} size={12.5} /></td>
                    <td style={{ padding: "8px", color: SUB, fontSize: 11.5 }}>{r.sig}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 10, fontSize: 12.5, color: SUB, lineHeight: 1.6 }}>
          Örnek: <strong>Süper Lig</strong>'de beraberlik piyasanın ima ettiğinden <Pp v="+11.3" size={12.5} />{" "}
          daha sık — taranan ligler arasındaki en yüksek beraberlik sapması. Tam tersine{" "}
          <strong>Scottish Premiership</strong>'te üç sinyalin üçü de <em>tersine</em> çalışıyor
          (<Pp v="-8.9" size={12.5} /> beraberlik). 14 ligin tamamı <em>İstatistikler → Piyasa Sapması</em> sekmesinde.
        </div>
      </div>

      {/* 3.2 Sezon ritmi */}
      <div style={card}>
        <div style={{ ...kicker, color: NEG }}>Katman 2 · Sezon Ritmi</div>
        <div style={q}>Takvimin hangi noktasında oynandığı</div>
        <div style={a}>
          Sezonu beş eşit dilime bölüyoruz (D1 başlangıç → D5 bitiş) ve ev avantajının piyasaya göre
          nasıl dalgalandığını izliyoruz. Çoğu ligde <strong>orta dilim (D3)</strong>, yani kışın ortası,
          ev avantajının çöktüğü nokta.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 16, marginTop: 10 }}>
          {[serieDonem, eredDonem].filter(Boolean).map((r) => (
            <div key={r.l} style={{ border: `1px solid ${LINE}`, borderRadius: 8, padding: "10px 12px" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: INK }}>{r.l}</div>
              <SezonSpark d={r.d} />
              <div style={{ fontSize: 12, color: SUB, marginTop: 2 }}>
                D3 çöküşü <Pp v={r.d[2]} size={12} />
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 10, fontSize: 12.5, color: SUB, lineHeight: 1.6 }}>
          Kırmızı nokta = sezonun en zayıf dilimi. <strong>Eredivisie</strong>'de bu düşüş{" "}
          <Pp v={eredDonem?.d[2]} size={12.5} />'a kadar iniyor.
        </div>
      </div>

      {/* 3.3 Hafta içi */}
      <div style={card}>
        <div style={{ ...kicker, color: NEG }}>Katman 3 · Hafta İçi Etkisi</div>
        <div style={q}>Salı–Çarşamba maçları farklı bir hayvan</div>
        <div style={a}>
          Yorgun kadrolar, seyahat ve düşük tempo hafta içi maçlarında ev avantajını eritir — ama piyasa
          bunu genelde fiyatlamaz. En çarpıcı örnek <strong>Serie A</strong>: hafta içi ev sahibinin
          kazanma oranı piyasanın imasının <Pp v={serieDonem?.mw} size={13} /> altında; boşalan avantaj
          beraberliğe kayıyor (<Pp v={serieDonem?.mwb} size={13} />).
        </div>
        <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
          {DONEM_DATA.filter((r) => ppNum(r.mw) !== null && Math.abs(ppNum(r.mw)) >= 2.5)
            .sort((x, y) => Math.abs(ppNum(y.mw)) - Math.abs(ppNum(x.mw)))
            .slice(0, 5)
            .map((r) => (
              <div key={r.l} style={{ border: `1px solid ${LINE}`, borderRadius: 8, padding: "8px 12px", minWidth: 120 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: INK }}>{r.l}</div>
                <div style={{ fontSize: 11, color: SUB, marginTop: 2 }}>hafta içi ev <Pp v={r.mw} size={12} /></div>
              </div>
            ))}
        </div>
      </div>

      {/* 3.4 Motivasyon */}
      <div style={card}>
        <div style={{ ...kicker, color: POS }}>Katman 4 · Motivasyon</div>
        <div style={q}>Puan tablosundaki durum, orana tam yansımıyor</div>
        <div style={a}>
          Takımları sezon içindeki hedeflerine göre grupluyoruz: şampiyon adayı, Avrupa yarışı,
          düşme hattı, rahat orta sıra. Piyasa bu motivasyonu çoğu zaman eksik okur.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14, marginTop: 12 }}>
          {[
            { t: "Süper Lig — lider evinde", d: superLider, renk: POS },
            { t: "Premier Lig — lider deplasmanda", d: plLiderDep, renk: POS },
            { t: "La Liga — düşme hattı evinde", d: laDusme, renk: POS },
          ].filter((x) => x.d).map(({ t, d, renk }) => {
            const fark = (d.rh - d.mh).toFixed(1);
            return (
              <div key={t} style={{ border: `1px solid ${LINE}`, borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: INK, marginBottom: 2 }}>{t}</div>
                <div style={{ fontSize: 11, color: DIM, marginBottom: 4 }}>{d.n} maç</div>
                <VsBars piyasa={d.mh} gercek={d.rh} renk={renk} />
                <div style={{ fontSize: 12, color: SUB, marginTop: 7, textAlign: "right" }}>
                  kör nokta <Pp v={fark} size={13} />
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 10, fontSize: 12.5, color: SUB, lineHeight: 1.6 }}>
          Örnek: Süper Lig'de şampiyonluğu kovalayan lider evinde <strong>%{superLider?.rh}</strong> kazanıyor,
          piyasa ise <strong>%{superLider?.mh}</strong> biçiyor — {(superLider?.rh - superLider?.mh).toFixed(1)} puanlık bir boşluk.
        </div>
      </div>

      {/* 3.5 Takım radarı */}
      <div style={card}>
        <div style={{ ...kicker, color: "#a2542b" }}>Katman 5 · Takım Radarı</div>
        <div style={q}>Bazı kulüpler yıllardır yanlış fiyatlanıyor</div>
        <div style={a}>
          Sapma her zaman lig geneli değil — kimi kulüpler için 30–90+ maç boyunca <strong>sistematik</strong>.
          Piyasa bunları ya sürekli hafife alıyor ya da sürekli abartıyor.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 20, marginTop: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: POS, marginBottom: 6 }}>PİYASA HAFİFE ALIYOR</div>
            {TAKIM_POS.slice(0, 3).map((r) => (
              <div key={r.t} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: `1px solid ${LINE}` }}>
                <span style={{ width: 68, flexShrink: 0 }}><Pp v={r.e} size={15} /></span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: INK }}>{r.t}</div>
                  <div style={{ fontSize: 11, color: DIM }}>{r.l} · {r.n} maç</div>
                </div>
              </div>
            ))}
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: NEG, marginBottom: 6 }}>PİYASA ABARTIYOR</div>
            {TAKIM_NEG.slice(0, 3).map((r) => (
              <div key={r.t} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: `1px solid ${LINE}` }}>
                <span style={{ width: 68, flexShrink: 0 }}><Pp v={r.e} size={15} /></span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: INK }}>{r.t}</div>
                  <div style={{ fontSize: 11, color: DIM }}>{r.l} · {r.n} maç</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══ 4. TAHMİNE NASIL GİRİYOR ══ */}
      <div style={sh}><span>🤖</span> Bu sapmalar tahmine nasıl giriyor?</div>
      <div style={card}>
        <div style={a}>
          Her maç, birbirinden bağımsız <strong>7 yapay zeka ajanı</strong> tarafından değerlendirilir.
          Ajanlar birbirinin raporunu görür, gerekirse itiraz eder — tek bir modelin yanılması sonucu çarpıtmaz.
          Yukarıdaki beş sapma katmanı, <strong>tahmin ajanının</strong> ham malzemesidir: model yalnızca
          kimin daha iyi olduğunu değil, <em>oranın nerede yanıldığını</em> da hesaba katar.
          <ul style={{ marginTop: 12, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
            <li><strong>Form analisti</strong> — son maçlar, ev/deplasman ayrımı, kafa kafaya geçmiş</li>
            <li><strong>İstatistik analisti</strong> — puan durumu, güç farkı (ELO), xG, H2H</li>
            <li><strong>Haber & kadro analisti</strong> — sakat/cezalı, teknik direktör, olası 11</li>
            <li><strong>Ev sahibi savunucusu</strong> — ev lehine en güçlü 3 argüman</li>
            <li><strong>Deplasman savunucusu</strong> — deplasman lehine en güçlü 3 argüman</li>
            <li><strong style={{ color: POS }}>Tahmin ajanı</strong> — raporları + piyasa oranlarını + yukarıdaki
              5 sapma katmanını birleştirip nihai olasılığı üretir</li>
            <li><strong>Güven ajanı</strong> — raporlar arası uyuma ve veri kalitesine bakıp 0–100 güven skorunu verir</li>
          </ul>
        </div>
      </div>

      {/* ══ 5. TAHMİN KARTINI OKUMA ══ */}
      <div style={sh}><span>🃏</span> Tahmin kartını nasıl okumalıyım?</div>

      <div style={card}>
        <div style={q}>Olasılık yüzdeleri</div>
        <div style={a}>
          <strong>Ev %52 / Beraberlik %24 / Deplasman %24</strong>: sistem ev galibiyetini %52 ihtimalle
          öngörüyor. Bu bir kehanet değil; geçmiş veri + piyasa oranı + sapma katmanlarına dayalı
          istatistiksel tahmindir. Yüzdeler toplamda %100 eder.
        </div>
      </div>

      <div style={card}>
        <div style={q}>Renk rozetleri</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "#374151" }}>
            <span style={badge("g")}>EV</span> Sistem ev sahibinin kazanmasını en olası görüyor
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "#374151" }}>
            <span style={badge("a")}>BER</span> Sistem beraberliği en olası görüyor
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "#374151" }}>
            <span style={badge("b")}>DEP</span> Sistem deplasman galibiyetini en olası görüyor
          </div>
        </div>
      </div>

      <div style={card}>
        <div style={q}>Güven skoru</div>
        <div style={a}>
          Sistemin kendi tahmininden ne kadar emin olduğunu 0–100 arası gösterir.
          <strong> 70+</strong>: veriler uyumlu, maç öngörülebilir. <strong>~40 ve altı</strong>: takımlar
          yakın, veriler çelişiyor ya da bilgi eksik — ihtiyatlı olmakta fayda var.
        </div>
      </div>

      <div style={card}>
        <div style={q}>Piyasa satırı (📊 Piyasa)</div>
        <div style={a}>
          22'den fazla bahis sitesinin oran ortalamasıdır. Sistemin olasılıklarını piyasanınkiyle
          yan yana görürsün: ikisi ayrışıyorsa, o maçta yukarıda anlattığımız türden bir sapma yakalanmış demektir.
        </div>
      </div>

      {/* ══ 6. SAYFALAR ══ */}
      <div style={sh}><span>🗺️</span> Sayfalar ne işe yarıyor?</div>
      {[
        ["Ana Sayfa", "Kalibrasyon araştırmamızın en çarpıcı bulgularını manşet formatında sunar; asıl işimiz olan yapay zeka tahminini başlatma ve sorgu motoruna geçiş buradan yapılır."],
        ["Tahmin sayfası", "Bu haftanın maçlarını lig lig gösterir. Bir maça 'Analiz Et' deyince 7 ajan devreye girer; sağ panelde raporu ve piyasa karşılaştırmasını okursun."],
        ["İstatistikler sayfası", "Genel analiz, lig bazında oranlar, skorlar, Sorgula (kendi filtrenle sorgu) ve Piyasa Sapması / Kalibrasyon sekmeleriyle tüm veri tablolarının tam referansı."],
        ["Geçmiş sayfası", "Analiz edilip sonucu belli olmuş maçlar; tahminlerin gerçek sonuçlarla karşılaştırması ve genel başarı oranı."],
      ].map(([t, d]) => (
        <div key={t} style={card}>
          <div style={q}>{t}</div>
          <div style={a}>{d}</div>
        </div>
      ))}

      {/* ══ 7. SSS ══ */}
      <div style={sh}><span>❓</span> Sık sorulan sorular</div>
      {[
        ["Bu tahminler kesin midir?",
         "Hayır. Futbolda hiçbir sonuç garanti değildir. Sistem olasılık hesaplar; gerçekleşen sonuç her zaman farklı olabilir. Geçmiş veriye dayalı istatistiksel bir araçtır."],
        ["Sapmalar neden lig lig değişiyor?",
         "Her ligin oyun kültürü, tempo ve seyirci etkisi farklı; bahis piyasasının o ligdeki alışkanlıkları da öyle. Bu yüzden tek bir 'ev avantajı' katsayısı yerine 14 ligi ayrı ayrı ölçüyoruz."],
        ["Veriler nereden geliyor?",
         "Puan durumu ve maç istatistikleri API-Football'dan, bahis oranları 22'den fazla bookmaker ortalamasından, sakatlık/kadro haberleri çeşitli spor sitelerinden otomatik alınır. Kalibrasyon bulguları 4 sezon × 14 lig × 24.265 maçlık arşivden hesaplanır."],
        ["Analizler ne zaman hazırlanıyor?",
         "Haftalık maçlar otomatik analiz edilip sisteme yüklenir; dilediğin maçı kendin de anlık analiz ettirebilirsin (üyelik planına bağlı)."],
        ["Hangi ligler destekleniyor?",
         "Süper Lig, Premier League, Championship, La Liga, Bundesliga, Serie A, Ligue 1, Eredivisie, Primeira Liga, Pro League ve daha fazlası. Sol menüdeki LİGLER bölümünde tam liste var."],
      ].map(([question, answer]) => (
        <div key={question} style={card}>
          <div style={q}>{question}</div>
          <div style={a}>{answer}</div>
        </div>
      ))}

      <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8,
                    padding: "14px 18px", fontSize: 13, color: "#1e40af", lineHeight: 1.7, marginTop: 8 }}>
        <strong>Şeffaflık:</strong> üretilen her tahmin kayda geçer ve maç bitince sonucuyla karşılaştırılır.
        İsabet oranı biriktikçe Geçmiş sayfasında yayımlanır.
      </div>
    </div>
  );
}
